import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Sparkles } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { COMPLIANCE_DOCUMENT_KINDS, type CategoryComplianceInput, type ComplianceDocumentKind, type ComplianceMatrixRow } from "@osmix/contracts/compliance";
import { api, type Category } from "@/api";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Select } from "@/components/ui/select";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";

/**
 * Документ о соответствии на узле дерева (17.10.2026, матрица 31.10.2026):
 * флажок наследуется подкатегориями, как характеристики и сетка; вид
 * документа, основание (ТР ТС), пояснение продавцу, маркировка и «без
 * исключений» - там же. Рекомендация матрицы засеяна миграцией 571 и видна
 * рядом со своим значением; применяется кнопкой «Применить рекомендации».
 */

export const complianceMatrixKey = ["compliance-matrix"] as const;
export const useComplianceMatrix = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: complianceMatrixKey,
      queryFn: _temp,
      staleTime: 60000
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
};
export type ResolvedCompliance = {
  required: boolean;
  kinds: string[];
  /** Узел, от которого пришло значение флажка; null - нигде не задан. */
  sourceId: string | null;
};

/** Значение каждого узла с наследованием - зеркало шлюза (resolveCategoryCompliance). */
export const resolveCompliance = (all: Category[]): Map<string, ResolvedCompliance> => {
  const byId = new Map(all.map(c => [c.id, c]));
  const out = new Map<string, ResolvedCompliance>();
  for (const c of all) {
    const chain: Category[] = [];
    for (let node: Category | undefined = c; node && !chain.includes(node) && chain.length <= all.length; node = node.parentId ? byId.get(node.parentId) : undefined) chain.push(node);
    const source = chain.find(node => node.certRequired != null);
    const kindsSource = chain.find(node => node.certKinds != null);
    out.set(c.id, {
      required: source?.certRequired === true,
      kinds: kindsSource?.certKinds ?? [],
      sourceId: source?.id ?? null
    });
  }
  return out;
};
const kindsText = (kinds: readonly string[] | null | undefined, t: (key: string) => string) => (kinds ?? []).map(kind => t(`compliance.kinds.${kind}`)).join(" / ");

/** Ячейка таблицы: требуется / не требуется, вид, основание, откуда унаследовано, рекомендация. */
export function ComplianceCell(t0) {
  const $ = _c(18);
  const {
    category,
    resolved,
    nameOf
  } = t0;
  const {
    t
  } = useTranslation();
  const matrix = useComplianceMatrix();
  let t1;
  if ($[0] !== category.certRequired || $[1] !== category.id || $[2] !== matrix.data || $[3] !== nameOf || $[4] !== resolved || $[5] !== t) {
    const row = matrix.data?.find(entry => entry.categoryId === category.id);
    const inherited = resolved?.sourceId && resolved.sourceId !== category.id;
    const rec = row?.recommended;
    const basis = row?.resolved.basis;
    let t2;
    if ($[7] !== resolved?.required || $[8] !== t) {
      t2 = resolved?.required ? <Badge><ShieldCheck className="size-3" /> {t("compliance.category.required")}</Badge> : <span className="text-muted-foreground">{t("compliance.category.notRequired")}</span>;
      $[7] = resolved?.required;
      $[8] = t;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    let t3;
    if ($[10] !== inherited || $[11] !== nameOf || $[12] !== resolved || $[13] !== t) {
      t3 = inherited && resolved?.sourceId && <span className="text-xs text-muted-foreground">{t("compliance.category.inheritedFrom", {
          name: nameOf(resolved.sourceId)
        })}</span>;
      $[10] = inherited;
      $[11] = nameOf;
      $[12] = resolved;
      $[13] = t;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    let t4;
    if ($[15] !== resolved || $[16] !== t) {
      t4 = resolved?.required && resolved.kinds.length > 0 && <span className="text-xs text-muted-foreground">{kindsText(resolved.kinds, t)}</span>;
      $[15] = resolved;
      $[16] = t;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    t1 = <div className="flex max-w-[320px] flex-col gap-1 whitespace-normal text-sm" data-testid={`category-compliance-${category.id}`} data-required={resolved?.required ? "true" : "false"}><span className="flex flex-wrap items-center gap-1">{t2}{row?.resolved.strict && resolved?.required && <Badge variant="outline" title={t("compliance.category.strictHint")}>{t("compliance.category.strictBadge")}</Badge>}</span>{t3}{t4}{basis && <span className="text-xs text-muted-foreground">{basis}</span>}{rec && rec.required != null && category.certRequired == null && <span className="text-xs text-amber-700" data-testid={`category-compliance-rec-${category.id}`}>{t("compliance.category.recommendation", {
          value: rec.required ? `${t("compliance.category.required").toLowerCase()}${rec.kinds?.length ? `: ${kindsText(rec.kinds, t)}` : ""}` : t("compliance.category.notRequired").toLowerCase()
        })}</span>}</div>;
    $[0] = category.certRequired;
    $[1] = category.id;
    $[2] = matrix.data;
    $[3] = nameOf;
    $[4] = resolved;
    $[5] = t;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}
type TriState = "" | "on" | "off";
export type ComplianceDraft = {
  required: TriState;
  kinds: string[] | null;
  basis: string;
  note: string;
  marking: string;
  strict: TriState;
};
const tri = (value: boolean | null | undefined): TriState => value == null ? "" : value ? "on" : "off";
const fromTri = (value: TriState): boolean | null => value === "" ? null : value === "on";

/** Черновик правки узла: свои значения (из матрицы, если она загружена). */
export const complianceDraftOf = (c: Category, row?: ComplianceMatrixRow): ComplianceDraft => ({
  required: tri(row ? row.own.required : c.certRequired),
  kinds: (row ? row.own.kinds : c.certKinds) ?? null,
  basis: row?.own.basis ?? "",
  note: row?.own.note ?? "",
  marking: row?.own.marking ?? "",
  strict: tri(row?.own.strict)
});

/** Что отправить шлюзу; null - ничего не поменялось. */
export const complianceInputOf = (before: ComplianceDraft, after: ComplianceDraft): CategoryComplianceInput | null => {
  const input: CategoryComplianceInput = {
    required: fromTri(after.required)
  };
  let changed = before.required !== after.required;
  if (JSON.stringify(before.kinds) !== JSON.stringify(after.kinds)) {
    input.kinds = after.kinds === null ? null : after.kinds.filter(k => (COMPLIANCE_DOCUMENT_KINDS as readonly string[]).includes(k)) as ComplianceDocumentKind[];
    changed = true;
  }
  for (const key of ["basis", "note", "marking"] as const) {
    if (before[key].trim() !== after[key].trim()) {
      input[key] = after[key].trim() || null;
      changed = true;
    }
  }
  if (before.strict !== after.strict) {
    input.strict = fromTri(after.strict);
    changed = true;
  }
  return changed ? input : null;
};

/** Правка узла: флажок, виды, «без исключений», основание и пояснение; рядом - рекомендация. */
export function ComplianceEditor(t0) {
  const $ = _c(124);
  const {
    value,
    onChange,
    parentRequired,
    categoryId
  } = t0;
  const {
    t
  } = useTranslation();
  const matrix = useComplianceMatrix();
  let effective;
  let rec;
  let t1;
  let t2;
  let t3;
  if ($[0] !== categoryId || $[1] !== matrix || $[2] !== onChange || $[3] !== parentRequired || $[4] !== t || $[5] !== value.required) {
    const row = categoryId ? matrix.data?.find(entry => entry.categoryId === categoryId) : undefined;
    rec = row?.recommended;
    effective = value.required === "" ? parentRequired : value.required === "on";
    const takeRecommendation = () => {
      if (!rec) {
        return;
      }
      onChange({
        required: tri(rec.required),
        kinds: rec.kinds ?? null,
        basis: rec.basis ?? "",
        note: rec.note ?? "",
        marking: rec.marking ?? "",
        strict: tri(rec.strict)
      });
    };
    t1 = "flex flex-col gap-2";
    t2 = "category-compliance-editor";
    t3 = rec && rec.required != null && <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs dark:border-amber-800 dark:bg-amber-950/30"><p className="font-medium">{t("compliance.category.recommendation", {
          value: rec.required ? `${t("compliance.category.required").toLowerCase()}${rec.kinds?.length ? `: ${kindsText(rec.kinds, t)}` : ""}` : t("compliance.category.notRequired").toLowerCase()
        })}</p>{rec.basis && <p className="text-muted-foreground">{rec.basis}</p>}<Button size="sm" variant="ghost" className="mt-1 h-7 px-2" onClick={takeRecommendation} data-testid="category-compliance-take-rec"><Sparkles className="size-3.5" /> {t("compliance.category.takeRecommendation")}</Button></div>;
    $[0] = categoryId;
    $[1] = matrix;
    $[2] = onChange;
    $[3] = parentRequired;
    $[4] = t;
    $[5] = value.required;
    $[6] = effective;
    $[7] = rec;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    effective = $[6];
    rec = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
  }
  const t4 = value.required;
  let t5;
  if ($[11] !== onChange || $[12] !== value) {
    t5 = e => onChange({
      ...value,
      required: e.target.value as TriState
    });
    $[11] = onChange;
    $[12] = value;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t) {
    t6 = t("compliance.category.edit");
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== parentRequired || $[17] !== t) {
    t7 = t("compliance.category.inheritNow", {
      state: parentRequired ? t("compliance.category.required") : t("compliance.category.notRequired")
    });
    $[16] = parentRequired;
    $[17] = t;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t7) {
    t8 = <option value="">{t7}</option>;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t) {
    t9 = t("compliance.category.on");
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t9) {
    t10 = <option value="on">{t9}</option>;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t) {
    t11 = t("compliance.category.off");
    $[25] = t;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11) {
    t12 = <option value="off">{t11}</option>;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t12 || $[31] !== t5 || $[32] !== t6 || $[33] !== t8 || $[34] !== value.required) {
    t13 = <Select value={t4} onChange={t5} aria-label={t6} data-testid="category-compliance-select">{t8}{t10}{t12}</Select>;
    $[29] = t10;
    $[30] = t12;
    $[31] = t5;
    $[32] = t6;
    $[33] = t8;
    $[34] = value.required;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== effective || $[37] !== onChange || $[38] !== t || $[39] !== value) {
    t14 = effective && <fieldset className="flex flex-col gap-1 text-xs"><legend className="mb-1 text-muted-foreground">{t("compliance.category.kinds")}</legend><label className="flex items-center gap-2"><input type="checkbox" checked={value.kinds === null} onChange={e_0 => onChange({
          ...value,
          kinds: e_0.target.checked ? null : []
        })} />{t("compliance.category.kindsInherit")}</label>{value.kinds !== null && COMPLIANCE_DOCUMENT_KINDS.map(kind => <label key={kind} className="flex items-center gap-2"><input type="checkbox" checked={value.kinds?.includes(kind) ?? false} onChange={e_1 => {
          const current = value.kinds ?? [];
          onChange({
            ...value,
            kinds: e_1.target.checked ? [...current, kind] : current.filter(k => k !== kind)
          });
        }} />{t(`compliance.kinds.${kind}`)}</label>)}</fieldset>;
    $[36] = effective;
    $[37] = onChange;
    $[38] = t;
    $[39] = value;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t) {
    t15 = t("compliance.category.strict");
    $[41] = t;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t15) {
    t16 = <span className="text-muted-foreground">{t15}</span>;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  const t17 = value.strict;
  let t18;
  if ($[45] !== onChange || $[46] !== value) {
    t18 = e_2 => onChange({
      ...value,
      strict: e_2.target.value as TriState
    });
    $[45] = onChange;
    $[46] = value;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== t) {
    t19 = t("compliance.category.inherit");
    $[48] = t;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== t19) {
    t20 = <option value="">{t19}</option>;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t) {
    t21 = t("compliance.category.strictOn");
    $[52] = t;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t21) {
    t22 = <option value="on">{t21}</option>;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== t) {
    t23 = t("compliance.category.strictOff");
    $[56] = t;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t23) {
    t24 = <option value="off">{t23}</option>;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t18 || $[61] !== t20 || $[62] !== t22 || $[63] !== t24 || $[64] !== value.strict) {
    t25 = <Select value={t17} onChange={t18} data-testid="category-compliance-strict">{t20}{t22}{t24}</Select>;
    $[60] = t18;
    $[61] = t20;
    $[62] = t22;
    $[63] = t24;
    $[64] = value.strict;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  let t26;
  if ($[66] !== t16 || $[67] !== t25) {
    t26 = <div className="flex flex-col gap-1 text-xs">{t16}{t25}</div>;
    $[66] = t16;
    $[67] = t25;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  let t27;
  if ($[69] !== t) {
    t27 = t("compliance.category.basis");
    $[69] = t;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== t27) {
    t28 = <span className="text-muted-foreground">{t27}</span>;
    $[71] = t27;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] !== onChange || $[74] !== value) {
    t29 = e_3 => onChange({
      ...value,
      basis: e_3.target.value
    });
    $[73] = onChange;
    $[74] = value;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== rec?.basis || $[77] !== t) {
    t30 = rec?.basis ?? t("compliance.category.basisPlaceholder");
    $[76] = rec?.basis;
    $[77] = t;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  let t31;
  if ($[79] !== t29 || $[80] !== t30 || $[81] !== value.basis) {
    t31 = <Input value={value.basis} onChange={t29} placeholder={t30} maxLength={300} data-testid="category-compliance-basis" />;
    $[79] = t29;
    $[80] = t30;
    $[81] = value.basis;
    $[82] = t31;
  } else {
    t31 = $[82];
  }
  let t32;
  if ($[83] !== t28 || $[84] !== t31) {
    t32 = <div className="flex flex-col gap-1 text-xs">{t28}{t31}</div>;
    $[83] = t28;
    $[84] = t31;
    $[85] = t32;
  } else {
    t32 = $[85];
  }
  let t33;
  if ($[86] !== t) {
    t33 = t("compliance.category.note");
    $[86] = t;
    $[87] = t33;
  } else {
    t33 = $[87];
  }
  let t34;
  if ($[88] !== t33) {
    t34 = <span className="text-muted-foreground">{t33}</span>;
    $[88] = t33;
    $[89] = t34;
  } else {
    t34 = $[89];
  }
  let t35;
  if ($[90] !== onChange || $[91] !== value) {
    t35 = e_4 => onChange({
      ...value,
      note: e_4.target.value
    });
    $[90] = onChange;
    $[91] = value;
    $[92] = t35;
  } else {
    t35 = $[92];
  }
  const t36 = rec?.note ?? "";
  let t37;
  if ($[93] !== t35 || $[94] !== t36 || $[95] !== value.note) {
    t37 = <textarea value={value.note} onChange={t35} placeholder={t36} maxLength={600} className="min-h-16 w-full rounded-md border bg-background p-2 text-xs outline-none focus:ring-2 focus:ring-ring" />;
    $[93] = t35;
    $[94] = t36;
    $[95] = value.note;
    $[96] = t37;
  } else {
    t37 = $[96];
  }
  let t38;
  if ($[97] !== t34 || $[98] !== t37) {
    t38 = <div className="flex flex-col gap-1 text-xs">{t34}{t37}</div>;
    $[97] = t34;
    $[98] = t37;
    $[99] = t38;
  } else {
    t38 = $[99];
  }
  let t39;
  if ($[100] !== t) {
    t39 = t("compliance.category.marking");
    $[100] = t;
    $[101] = t39;
  } else {
    t39 = $[101];
  }
  let t40;
  if ($[102] !== t39) {
    t40 = <span className="text-muted-foreground">{t39}</span>;
    $[102] = t39;
    $[103] = t40;
  } else {
    t40 = $[103];
  }
  let t41;
  if ($[104] !== onChange || $[105] !== value) {
    t41 = e_5 => onChange({
      ...value,
      marking: e_5.target.value
    });
    $[104] = onChange;
    $[105] = value;
    $[106] = t41;
  } else {
    t41 = $[106];
  }
  const t42 = rec?.marking ?? "";
  let t43;
  if ($[107] !== t41 || $[108] !== t42 || $[109] !== value.marking) {
    t43 = <Input value={value.marking} onChange={t41} placeholder={t42} maxLength={300} />;
    $[107] = t41;
    $[108] = t42;
    $[109] = value.marking;
    $[110] = t43;
  } else {
    t43 = $[110];
  }
  let t44;
  if ($[111] !== t40 || $[112] !== t43) {
    t44 = <div className="flex flex-col gap-1 text-xs">{t40}{t43}</div>;
    $[111] = t40;
    $[112] = t43;
    $[113] = t44;
  } else {
    t44 = $[113];
  }
  let t45;
  if ($[114] !== t1 || $[115] !== t13 || $[116] !== t14 || $[117] !== t2 || $[118] !== t26 || $[119] !== t3 || $[120] !== t32 || $[121] !== t38 || $[122] !== t44) {
    t45 = <div className={t1} data-testid={t2}>{t3}{t13}{t14}{t26}{t32}{t38}{t44}</div>;
    $[114] = t1;
    $[115] = t13;
    $[116] = t14;
    $[117] = t2;
    $[118] = t26;
    $[119] = t3;
    $[120] = t32;
    $[121] = t38;
    $[122] = t44;
    $[123] = t45;
  } else {
    t45 = $[123];
  }
  return t45;
}

/** «Применить рекомендации»: флажок и виды из матрицы на узлах, где площадка ещё ничего не задала. */
export function ApplyRecommendedButton({
  all,
  onDone
}: {
  all: Category[];
  onDone: (updated: number) => void;
}) {
  const {
    t
  } = useTranslation();
  const matrix = useComplianceMatrix();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const byId = new Map(all.map(c => [c.id, c]));
  const pending = (matrix.data ?? []).filter(row => row.own.required == null && row.recommended.required != null && !row.archived);
  const label = (row_0: ComplianceMatrixRow) => {
    const parent = row_0.parentId ? byId.get(row_0.parentId) : undefined;
    return parent ? `${parent.name} / ${row_0.name}` : row_0.name;
  };
  const on = pending.filter(row_1 => row_1.recommended.required);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.compliance.applyRecommended();
      await matrix.refetch();
      onDone(res.updated.length);
      setOpen(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return <>
      <Button variant="outline" onClick={() => setOpen(true)} disabled={pending.length === 0} title={t("compliance.category.recommendedHint")} data-testid="category-compliance-recommended">
        <ShieldCheck className="size-4" /> {t("compliance.category.applyRecommended")}
      </Button>
      <ConfirmDialog open={open} busy={busy} title={t("compliance.category.applyRecommendedTitle")} confirmLabel={t("compliance.category.applyRecommended")} onConfirm={() => void run()} onClose={() => setOpen(false)}>
        <p className="text-sm">
          {t("compliance.category.applyRecommendedBody", {
          count: pending.length,
          on: on.length,
          names: on.map(label).join(", ")
        })}
        </p>
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
      </ConfirmDialog>
    </>;
}
function _temp() {
  return api.compliance.matrix();
}